import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  selectCrossProductParams,
  clearParams,
} from '@stores/crossProductAction';
import HttpClient from '@infra/http-client';
import { sendInternalAnalytics } from '@utils/analytics';

const client = HttpClient('');

const ANALYTICS_EVENT = {
  eventCategory: '[CROSS PRODUCT][AUTO ACTION]',
  eventAction: 'autentica-ui',
  eventLabel: 'cross-product-execution',
};

/**
 * Hook que executa a ação automática do cross-product no autentica-ui.
 *
 * Comportamento:
 * - Se params existem no Redux, faz fetch de fluxos ativos.
 * - Se existem fluxos: navega para /solicitacoes (a página consome params e abre drawer).
 * - Se não existem fluxos: navega para /construtor/novo-fluxo.
 * - Limpa params após a navegação quando vai para o builder.
 * - Params são mantidos no Redux para consumo pela página de Solicitações.
 */
export function useCrossProductExecution() {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useSelector(selectCrossProductParams);
  const executingRef = useRef(false);

  useEffect(() => {
    if (!params) return;
    if (executingRef.current) return;

    const execute = async () => {
      executingRef.current = true;

      try {
        const response = await client.get<any>({
          action: 'fluxos/ativos',
          params: { index: 1, size: 1 },
          loading: false,
        });

        const hasActiveFlows = response?.data?.jornadas?.length > 0;

        if (hasActiveFlows) {
          // Usuário possui fluxos ativos — navegar para solicitações
          // Os params permanecem no Redux para a página consumir e abrir o drawer
          history.push('/solicitacoes');
          sendInternalAnalytics({
            ...ANALYTICS_EVENT,
            eventLabel: 'open-request-drawer',
          });
        } else {
          // Sem fluxos ativos — redirecionar para criação de fluxo
          history.push('/construtor/novo-fluxo');
          dispatch(clearParams());
          sendInternalAnalytics({
            ...ANALYTICS_EVENT,
            eventLabel: 'redirect-builder',
          });
        }
      } catch {
        // Em caso de erro, limpa params sem executar ação
        dispatch(clearParams());
      } finally {
        executingRef.current = false;
      }
    };

    execute();
  }, [params, dispatch, history]);
}
